var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { Fragment, memo, useContext, useMemo, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { TextLink } from '../TextLink/index';
import { PaginationContext } from './PaginationContext';
const currentPageBorderWidth = 2;
const expandedPageNumberStyles = css `
  ${theme.typeStyles.bodySmallStyles}
  align-items: center;
  display: flex;
  justify-content: center;
`;
const expanderStyles = css `
  margin-left: ${theme.spacing.x3};
  margin-right: ${theme.spacing.x3};
`;
const pageNumberStyles = css `
  border-bottom-color: transparent;
  border-bottom-style: solid;
  border-bottom-width: ${styleUtils.rem(currentPageBorderWidth)};
  color: ${theme.colors.gray700};
  padding-left: ${theme.spacing.x3};
  padding-right: ${theme.spacing.x3};
`;
const currentPageNumberStyles = css `
  ${pageNumberStyles}
  ${styleUtils.borderBox}
  border-bottom-color: ${theme.colors.gray700};
  padding-bottom: ${theme.spacing.x2};
  padding-top: ${theme.spacing.x2};
`;
const ExpandedPageNumbers = () => {
    const { currentPageNumber, makeHref, onPageClick, totalPages, } = (useContext(PaginationContext));
    const pageNumbers = (useMemo(() => {
        // get initial page range: 2 pages on either side of current page
        const rangeOnEitherSide = 2;
        const left = currentPageNumber - rangeOnEitherSide;
        const right = currentPageNumber + rangeOnEitherSide;
        // update page range so we have 5 pages to display
        const rightBoundary = left < 1 ? (right + (1 - left)) : right;
        const leftBoundary = rightBoundary > totalPages ? (left - (right - totalPages)) : left;
        // return range of five page numbers (if we have enough pages),
        // plus first and last page number if those are not already included in the range
        return (Array(totalPages)
            .fill(undefined)
            .map((value, index) => (index + 1))
            .filter((pageNumber) => (pageNumber === 1
            || pageNumber === totalPages
            || (pageNumber >= leftBoundary
                && pageNumber <= rightBoundary)))
            .map((pageNumber, index, pages) => ({
            hasExpander: (index < (pages.length - 1)
                && (pages[index + 1] - pageNumber > 1)),
            isCurrentPage: (pageNumber === currentPageNumber),
            pageNumber,
            rel: (pageNumber === currentPageNumber - 1
                ? 'prev'
                : pageNumber === currentPageNumber + 1
                    ? 'next'
                    : undefined),
        })));
    }, [
        currentPageNumber,
        totalPages,
    ]));
    const pages = (useMemo(() => (pageNumbers
        .map((_a) => {
        var { pageNumber } = _a, otherProps = __rest(_a, ["pageNumber"]);
        return (Object.assign(Object.assign(Object.assign({}, otherProps), { onClick: (onPageClick
                ? (event) => {
                    event.preventDefault();
                    onPageClick(pageNumber);
                }
                : () => { }), pageNumber }), (makeHref
            && {
                href: (makeHref(pageNumber)),
            })));
    })), [
        makeHref,
        pageNumbers,
        onPageClick,
    ]));
    return (_jsx("div", Object.assign({ css: expandedPageNumberStyles }, { children: pages
            .map(({ hasExpander, href, isCurrentPage, onClick, pageNumber, rel, }) => (_jsxs(Fragment, { children: [_jsx("div", Object.assign({ css: isCurrentPage
                        ? currentPageNumberStyles
                        : pageNumberStyles }, { children: _jsx(TextLink, { href: href, onClick: onClick, rel: rel, size: "small", text: String(pageNumber), type: "tertiaryAlternate" }) })), hasExpander
                    && (_jsx("div", Object.assign({ css: expanderStyles }, { children: "..." })))] }, pageNumber))) })));
};
const MemoizedExpandedPageNumbers = memo(ExpandedPageNumbers);
export { MemoizedExpandedPageNumbers as ExpandedPageNumbers };
